import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Button, useTheme } from '@mui/material';
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/20/solid';
import { Portal } from '../Portals';
import useFocusTrap from '../../hooks/useFocusTrap';

const SkillAliasesModal = ({
  isOpen,
  onClose,
  skillName,
  existingAliases = [],
  onSubmit
}) => {
  const theme = useTheme();
  const [aliasInput, setAliasInput] = useState('');
  const modalRef = useFocusTrap(isOpen , onClose)
  useEffect(() => {
    if (isOpen && existingAliases.length > 0) {
      setAliasInput(existingAliases.join(', '));
    } else if (isOpen) {
      setAliasInput('');
    }
  }, [isOpen, existingAliases]);

  const handleSubmit = () => {
    const aliases = aliasInput
      .split(',')
      .map(alias => alias.trim())
      .filter(alias => alias !== '');

    onSubmit(skillName, aliases);
    handleClose();
  };

  const handleClose = () => {
    setAliasInput('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Portal>
      <div className="fixed inset-0 flex items-start justify-center z-50 bg-black bg-opacity-80">
        <div
          ref={modalRef}
          className="rounded-lg w-full max-w-47rem overflow-hidden relative flex flex-col justify-between max-sm:mx-2 my-auto"
          style={{
            maxHeight: "95vh",
            overflow: "auto",
            backgroundColor: theme.palette.primary.main
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="alias-modal-title"
        >
          <div>
            <div className="flex items-center justify-between py-4 px-5">
              <h2
                id="alias-modal-title"
                className="font-semibold capitalize"
                style={{ color: theme.palette.text.main }}
              >
                {skillName} - Edit Aliases
              </h2>
              <button
                onClick={handleClose}
                aria-label="Close pop-up"
                tabIndex={0}
                className="bg-black text-white rounded-full w-6 h-6 flex items-center justify-center"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>

            <div className="border-t border-b py-9 px-5 sm:px-14"
              style={{
                borderColor: theme.palette.secondary.v3
              }}
            >
              <p
                className="mb-3 font-bold"
                style={{ color: theme.palette.text.main }}
              >
                Add aliases that can also match the selected skill as comma separated values
              </p>
              <input
                type="text"
                value={aliasInput}
                onChange={(e) => setAliasInput(e.target.value)}
                placeholder="Frontend, front end, FE"
                className="w-full border rounded px-3 py-2 outline-none font-medium"
                style={{
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.text.dark,
                  borderColor: theme.palette.secondary.v4,
                }}
              />
            </div>

            <div className="flex justify-between py-3 px-5">
              <Button
                sx={{
                  color: theme.palette.text.main,
                  textTransform: 'none',
                  padding: '0',
                  '&.Mui-disabled': {
                    backgroundColor: theme.palette.primary.main
                  },
                  '&:hover': {
                    backgroundColor: theme.palette.primary.main
                  }
                }}
                onClick={handleClose}
              >
                <span className="flex gap-1 items-center justify-center">
                  <ArrowLeftIcon className="w-4 h-4 " />Back
                </span>
              </Button>
              <Button
                sx={
                  {
                    backgroundColor: theme.palette.info.main,
                    color: theme.palette.text.light,
                    padding: "0 10px"
                  }
                }
                onClick={handleSubmit}>
                <span className="flex gap-1 items-center justify-center">
                  Submit <ArrowRightIcon className="w-4 h-4 " />
                </span>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
};

export default SkillAliasesModal;
